import { Snowflake } from "@sapphire/snowflake";

import { getCorrelationId } from "./correlationId";

const EPOCH_OFFSET_MS = Date.UTC(2026, 0, 1);

/**
 * Sapphire splits the machine id into two 5-bit fields (`workerId` and
 * `processId`, each masked to `0b11111`) rather than exposing a single 10-bit
 * value — so the derived id is split across both. Assigning the raw value to
 * either field alone would silently truncate it to 32.
 */
const MACHINE_ID_BITS = 1024;

/**
 * Maps a tab's `correlationId` onto the 10-bit machine id.
 *
 * This was `Math.floor(Math.random() * 1024)`, with a comment claiming it
 * preserved "the full 1024-way collision resistance". A random draw is a
 * birthday-bound problem instead: two of 10 concurrent tabs share a value ~4.3%
 * of the time, ~17% at 20 tabs, ~70% at 50. Two tabs holding the same machine
 * id and generating in the same millisecond at the same increment mint
 * byte-identical ids.
 *
 * `correlationId` is already unique per tab, so deriving from it makes the
 * machine id unique wherever the correlation id is — turning a probabilistic
 * collision into one that occurs only if the correlation ids themselves collide.
 *
 * FNV-1a rather than something shorter because it consumes the whole string:
 * real correlation ids are v4 UUIDs sharing a fixed version nibble and variant
 * bits, so a hash reading only a prefix would map many tabs together.
 */
export function deriveTabMachineId(correlationId: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < correlationId.length; i++) {
    hash ^= correlationId.charCodeAt(i);
    // The FNV prime (16777619), applied via shifts so the intermediate stays
    // inside 32-bit integer range.
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0) % MACHINE_ID_BITS;
}

const generator = new Snowflake(EPOCH_OFFSET_MS);

/**
 * Applied on the first `nextId()` rather than at module load.
 *
 * `getCorrelationId()` reads sessionStorage and may mint a UUID, so calling it
 * during module evaluation would do that work at import time — in tests that
 * never emit an event, and before the host is ready. Deferring keeps the import
 * side-effect-free.
 *
 * Cached so the machine id stays fixed for the tab's lifetime. The correlation
 * id changes on `resetAnalyticsIdentity()` at logout, and re-deriving there
 * would move the machine id mid-session (measured: 531 -> 372), attributing the
 * same tab's events to a different source. It would also put
 * `getCorrelationId()`'s sessionStorage read on every single event.
 *
 * Not a collision concern: sapphire's increment is one counter that keeps
 * advancing across machine-id changes, so ids stay distinct either way. Checked
 * rather than assumed — an earlier version of this comment claimed otherwise.
 */
let machineIdApplied = false;

export function nextId(): string {
  if (!machineIdApplied) {
    // getCorrelationId is guarded internally: blocked sessionStorage falls back
    // to an in-memory id rather than throwing (see correlationId.ts).
    const machineId = deriveTabMachineId(getCorrelationId());
    generator.workerId = BigInt(machineId >> 5);
    generator.processId = BigInt(machineId & 0b11111);
    machineIdApplied = true;
  }
  return generator.generate().toString();
}
